import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { LevelConfigDto } from '@/api/dto';
import { devRoutes } from '@/api/routes';
import { getPrefs, usePrefs } from '@/meta/prefsStore';
import { LevelResultPanel } from '@/screens/LevelResultPanel';
import { useLevelHost } from '@/screens/useLevelHost';

// Дев-экран wildcard-уровня (дизайн 2026-07-08): монтирует launch-route по
// hand-authored уровню, отдаваемому GET /api/dev/wildcard/{slug}/config БЕЗ
// гейтинга/auth. Тест механики инструментов вне карты/меты — прогресс НЕ пишется,
// сюжет-ролики/«далее»/пейволл отсутствуют. Живёт только в дев-сборке (App.tsx
// гейтит роут по VITE_DEV_TOOLS → в проде выпиливается три-шейкингом).

/** source: 'wildcard' — hand-authored файл level-data/wildcard/<slug>.json;
 *  'shared' — снимок, отправленный из конструктора кнопкой «Поделиться» (/shared/<id>). */
export function WildcardScreen({ source = 'wildcard' }: { source?: 'wildcard' | 'shared' }) {
  const { slug } = useParams<{ slug: string }>();
  // ?skin=<world-NN>&hat=<id> — примерить облик и шапку на стенде. В игре их кладёт мета
  // из прогресса игрока (открываются за звёзды мира), но приёмка арта идёт до всякой меты:
  // автору нужно сравнить три шапки мира на одном забеге, меняя параметр в адресе.
  const params = useSearchParams()[0];
  const skin = params.get('skin') ?? undefined;
  const hat = params.get('hat') ?? undefined;
  // ?record=1 — съёмка сцены трейлера: плеер не крутит rAF, а выдаёт кадры по
  // window.__rec (players/launch-route recorder.ts). Гоняет рекордер
  // marketing/trailer-01/scripts/record.mjs, руками этот режим не нужен.
  const recording = params.get('record') === '1';
  const prefs = usePrefs();

  const host = useLevelHost({
    enabled: !!slug,
    // смена любого параметра в адресе пересоздаёт уровень
    key: `${source}|${slug ?? ''}|${skin ?? ''}|${hat ?? ''}|${recording ? 'rec' : ''}`,
    skin,
    hat,
    ...(recording ? { capabilities: ['record'] } : {}),
    // дев-харнесс: фоновая музыка мешает при разработке механики — всегда
    // заглушена (music=0), SFX живые из настроек меты
    getVolumes: () => ({ music: 0, sfx: getPrefs().sfx }),
    async loadConfig() {
      // wildcard-конфиг — обычный fetch, БЕЗ авторизации и без серверного гейта
      const shared = source === 'shared';
      const resp = await fetch(shared ? devRoutes.sharedLevel(slug!) : devRoutes.wildcard(slug!));
      if (!resp.ok) {
        throw new Error(resp.status !== 404 ? `config ${resp.status}`
          : shared ? `Нет уровня по ссылке «${slug}» — проверь ссылку целиком`
          : `Нет wildcard-уровня «${slug}» — проверь имя файла level-data/wildcard/${slug}.json`);
      }
      return (await resp.json()) as LevelConfigDto;
    },
  });

  // громкость SFX доезжает до идущего уровня живьём; музыка глушится всегда
  // (контракт 1.1, setVolumes опционален)
  useEffect(() => {
    host.instanceRef.current?.setVolumes?.(0, prefs.sfx);
  }, [prefs.sfx]);

  return (
    <main
      data-testid="screen-wildcard"
      style={{
        position: 'fixed', inset: 0, overflow: 'hidden',
        background: '#1E1A14', color: '#F4ECDD',
      }}
    >
      {/* плашка «wildcard: <slug>» убрана: её угол занимает миникарта уровня
          (players/launch-route ui/minimap) — она же и есть маркер стенда */}
      <div ref={host.mountRef} data-testid="wildcard-mount" style={{ position: 'absolute', inset: 0 }} />
      {host.phase === 'loading' && (
        <p data-testid="wildcard-loading" className="play-note">Загрузка уровня…</p>
      )}
      {host.phase === 'error' && (
        <output data-testid="wildcard-error" className="play-note err">
          Не получилось: {host.error}
        </output>
      )}
      {host.phase === 'result' && host.result && (
        // окно результата общее, но действия — только «Ещё раз» (перезапуск)
        <LevelResultPanel result={host.result} testId="wildcard-result" starsTestId="wildcard-stars"
          actions={
            <button
              className="btn btn-primary btn-lg"
              data-testid="wildcard-again"
              onClick={host.restart}
            >
              Ещё раз
            </button>
          }
        />
      )}
    </main>
  );
}
